/**
 * Role configuration — single source of truth for role-aware UI/agent behavior.
 *
 * Each role drives:
 *   - Top-nav role switcher label/icon
 *   - Welcome copy in the empty chat state
 *   - Quick-action cards
 *   - Accent color (CSS custom property)
 *   - Agent context payload (sent with every chat.send so the agent adapts)
 */

import type { UserRole } from '../types/gateway';

export interface RoleQuickAction {
  /** Action title shown on the card */
  title: string;
  /** Short explanation under the title */
  description: string;
  /** Emoji / unicode glyph icon */
  icon: string;
  /** Prompt text injected into the input when card is clicked */
  prompt: string;
}

export interface RoleConfig {
  key: UserRole;
  /** Short label e.g. "Field Contractor" */
  label: string;
  /** Glyph displayed in the role switcher */
  icon: string;
  /** Accent CSS variable name (without `--` prefix) used to theme the UI */
  accent: 'amber' | 'purple' | 'green' | 'blue';
  /** One-line description shown under the label in the dropdown */
  description: string;
  /** "Goal" line sent to the agent on every message */
  goal: string;
  /** Welcome line shown in the empty chat state */
  welcomeHeadline: string;
  /** Sub-line describing how Foreman helps in this role */
  welcomeSubline: string;
  /** Behavior contract sent to the agent so it adapts tone/output */
  expectedBehavior: string;
  /** Cards rendered when chat is empty */
  quickActions: RoleQuickAction[];
}

const FIELD: RoleConfig = {
  key: 'field',
  label: 'Field Contractor',
  icon: '\u{1F6E0}\u{FE0F}', // hammer-and-wrench
  accent: 'amber',
  description: 'Capture job details and send to office',
  goal: 'Capture job details and send to office',
  welcomeHeadline: "Hey {name}, I\u2019m Foreman.",
  welcomeSubline:
    "I read live BuilderTrend \u2014 job briefings, delivery status, photo progress, and punch lists. Name a job and go.",
  expectedBehavior:
    'Ask simple follow-up questions, help verify measurements, remind the user about missing photos/details, and confirm when the office has everything needed.',
  quickActions: [
    {
      title: 'Job Briefing',
      description: 'One-page status before you head out',
      icon: '\u{1F4CB}',
      prompt: 'Give me the briefing for ',
    },
    {
      title: 'Delivery Status',
      description: 'What\u2019s received, in the bay, or on site',
      icon: '\u{1F69A}',
      prompt: 'What\u2019s the delivery status on ',
    },
    {
      title: 'Job Progress',
      description: 'How far along, read from the site photos',
      icon: '\u{1F4F8}',
      prompt: 'How far along is ',
    },
    {
      title: 'Punch List',
      description: 'Build the install punch list for a job',
      icon: '\u{2705}',
      prompt: 'Build the punch list for ',
    },
  ],
};

const DESIGNER: RoleConfig = {
  key: 'designer',
  label: 'Kitchen Designer',
  icon: '\u{1F3A8}', // artist palette
  accent: 'purple',
  description: 'Design kitchens and build scopes',
  goal: 'Design kitchens and build scopes',
  welcomeHeadline: "Hey {name}, I\u2019m Foreman.",
  welcomeSubline:
    "I check quotes before ordering, brief you on any job, track material readiness, and do tile math off the drawings \u2014 all from live BuilderTrend.",
  expectedBehavior:
    'Help organize design requirements, identify missing information, suggest material options, flag scope gaps, and keep the design aligned with pricing needs.',
  quickActions: [
    {
      title: 'Quote Check',
      description: 'Is the quote ready to order? TBDs, gaps, exclusions',
      icon: '\u{1F50D}',
      prompt: 'Run a quote check on ',
    },
    {
      title: 'Job Briefing',
      description: 'One-page status: money, POs, COs, flags',
      icon: '\u{1F4CB}',
      prompt: 'Give me the briefing for ',
    },
    {
      title: 'Material Readiness',
      description: 'What\u2019s ordered vs. still needs ordering',
      icon: '\u{1F4E6}',
      prompt: 'What\u2019s the material readiness on ',
    },
    {
      title: 'Tile Takeoff',
      description: 'Square footage from the design drawings',
      icon: '\u{1F9EE}',
      prompt: 'Run a tile takeoff for ',
    },
  ],
};

const ACCOUNTING: RoleConfig = {
  key: 'accounting',
  label: 'Accounting / Office',
  icon: '\u{1F4B0}', // money bag
  accent: 'green',
  description: 'Price jobs and manage bids',
  goal: 'Price jobs and manage bids',
  welcomeHeadline: "Hey {name}, I\u2019m Foreman.",
  welcomeSubline:
    "I track change-order markup, under-billed jobs, vendor pricing history, and the live WIP picture \u2014 straight from BuilderTrend.",
  expectedBehavior:
    'Pull together submitted details, calculate pricing, surface risks or missing data, show margin impact, and help create a polished final bid.',
  quickActions: [
    {
      title: 'Change Orders',
      description: 'CO markup vs. cost on a job',
      icon: '\u{1F4CA}',
      prompt: 'Show the change orders for ',
    },
    {
      title: 'Under-billed Jobs',
      description: 'Who\u2019s behind on billing across the book',
      icon: '\u{1F9FE}',
      prompt: 'Who\u2019s under-billed right now?',
    },
    {
      title: 'Vendor Price History',
      description: 'e.g. \u201cWhat do we pay Medallion for cabinetry\u201d',
      icon: '\u{1F4B0}',
      prompt: 'What do we pay ',
    },
    {
      title: 'Work In Progress',
      description: 'Booked, earned-to-date, and backlog',
      icon: '\u{1F3D7}\u{FE0F}',
      prompt: 'What\u2019s our work in progress picture?',
    },
  ],
};

const OPERATIONS: RoleConfig = {
  key: 'operations',
  label: 'Operations',
  icon: '\u{1F3D7}\u{FE0F}', // building construction
  accent: 'blue',
  description: 'Track job status, schedules, and deliveries',
  goal: 'Track job status, schedules, and deliveries',
  welcomeHeadline: "Hey {name}, I\u2019m Foreman.",
  welcomeSubline:
    "I track job status, schedule slip, deliveries, and punch lists across the whole book \u2014 live from BuilderTrend.",
  expectedBehavior:
    'Give fast, deterministic job status; surface schedule slip and delivery gaps proactively; keep answers short and phone-readable.',
  quickActions: [
    {
      title: 'Job Status',
      description: 'One-page briefing: money, POs, COs, flags',
      icon: '\u{1F4CB}',
      prompt: 'Give me the briefing for ',
    },
    {
      title: 'Behind Schedule',
      description: 'Jobs running past their estimated days',
      icon: '\u{23F0}',
      prompt: 'Which jobs are behind schedule?',
    },
    {
      title: 'Delivery Status',
      description: 'What\u2019s received, in the bay, or on site',
      icon: '\u{1F69A}',
      prompt: 'What\u2019s the delivery status on ',
    },
    {
      title: 'Punch List',
      description: 'Build the install punch list for a job',
      icon: '\u{2705}',
      prompt: 'Build the punch list for ',
    },
  ],
};

/** Admin keeps its own minimal config so legacy users with admin uiRole don't break. */
const ADMIN: RoleConfig = {
  key: 'admin',
  label: 'Admin',
  icon: '\u{1F511}',
  accent: 'blue',
  description: 'Manage training data and system config',
  goal: 'Manage training data and system config',
  welcomeHeadline: "Hey {name}, I\u2019m Foreman.",
  welcomeSubline:
    "Billing and portfolio at a glance \u2014 or pick a role above for role-tailored quick actions.",
  expectedBehavior:
    'Treat the user as an admin with access to training data and system management.',
  quickActions: [
    {
      title: 'Under-billed Jobs',
      description: 'Who\u2019s behind on billing across the book',
      icon: '\u{1F9FE}',
      prompt: 'Who\u2019s under-billed right now?',
    },
    {
      title: 'Weekly Digest',
      description: 'The Monday portfolio roll-up, on demand',
      icon: '\u{1F4EC}',
      prompt: 'Give me the weekly digest',
    },
    {
      title: 'Money Losers',
      description: 'Jobs bleeding profit or below target margin',
      icon: '\u{1F4C9}',
      prompt: 'Show me the money losers',
    },
    {
      title: 'Work In Progress',
      description: 'Booked, earned-to-date, and backlog',
      icon: '\u{1F3D7}\u{FE0F}',
      prompt: 'What\u2019s our work in progress picture?',
    },
  ],
};

export const ROLE_CONFIG: Record<UserRole, RoleConfig> = {
  field: FIELD,
  designer: DESIGNER,
  accounting: ACCOUNTING,
  operations: OPERATIONS,
  admin: ADMIN,
};

/** Roles that appear in the user-facing dropdown. Admin is handled elsewhere. */
export const SWITCHABLE_ROLES: RoleConfig[] = [FIELD, DESIGNER, ACCOUNTING, OPERATIONS];

export function getRoleConfig(role: UserRole): RoleConfig {
  return ROLE_CONFIG[role] || FIELD;
}

/** Build the agent context payload sent on every chat.send. */
export function buildAgentRoleContext(role: UserRole) {
  const cfg = getRoleConfig(role);
  return {
    active_role: cfg.key,
    role_label: cfg.label,
    role_goal: cfg.goal,
    expected_agent_behavior: cfg.expectedBehavior,
  };
}
